import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, unref as _unref, createVNode as _createVNode, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, vModelCheckbox as _vModelCheckbox, withDirectives as _withDirectives, createCommentVNode as _createCommentVNode, normalizeClass as _normalizeClass, createBlock as _createBlock } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "fixed inset-0 z-50 flex items-center justify-center p-4"
}
const _hoisted_2 = { class: "relative bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[88vh] flex flex-col" }
const _hoisted_3 = { class: "flex items-center justify-between px-5 py-4 border-b border-dashboard-border" }
const _hoisted_4 = { class: "flex items-center gap-3" }
const _hoisted_5 = { class: "p-2 bg-emerald-50 rounded-lg" }
const _hoisted_6 = { class: "px-5 py-2.5 border-b border-dashboard-border flex items-center justify-between gap-3 flex-wrap" }
const _hoisted_7 = { class: "text-xs text-dashboard-muted" }
const _hoisted_8 = { class: "text-dashboard-text" }
const _hoisted_9 = { class: "flex items-center gap-2" }
const _hoisted_10 = { class: "px-5 py-4 overflow-y-auto space-y-5" }
const _hoisted_11 = { class: "flex items-center justify-between mb-2" }
const _hoisted_12 = { class: "text-sm font-semibold text-dashboard-text" }
const _hoisted_13 = { class: "text-[11px] text-dashboard-muted flex items-center gap-1.5 cursor-pointer" }
const _hoisted_14 = ["checked", "onChange"]
const _hoisted_15 = { class: "grid grid-cols-1 sm:grid-cols-2 gap-2" }
const _hoisted_16 = ["onUpdate:modelValue"]
const _hoisted_17 = { class: "min-w-0" }
const _hoisted_18 = { class: "text-sm text-dashboard-text" }
const _hoisted_19 = {
  key: 0,
  class: "ml-1 text-[10px] px-1 py-0.5 rounded bg-sky-50 text-sky-700 align-middle"
}
const _hoisted_20 = { class: "px-5 py-4 border-t border-dashboard-border flex items-center justify-between gap-3" }
const _hoisted_21 = {
  key: 0,
  class: "text-xs text-red-600"
}
const _hoisted_22 = {
  key: 1,
  class: "text-xs text-dashboard-muted"
}
const _hoisted_23 = { class: "flex items-center gap-2" }
const _hoisted_24 = ["disabled"]

import { ref, reactive, computed, watch } from 'vue'
import { FileSpreadsheet, X, Download, Loader2 } from 'lucide-vue-next'
import {
  EXPORT_CATALOG, buildAndDownload, fetchLoginAggregates,
  type ExportFilters, type ExportMeta,
} from '@/utils/dashboardExport'


export default /*@__PURE__*/_defineComponent({
  __name: 'ExportModal',
  props: {
    open: { type: Boolean },
    overview: {},
    courriers: {},
    departs: {},
    affectations: {},
    params: {}
  },
  emits: ["close", "done"],
  setup(__props: any, { emit: __emit }) {

const props = __props
const emit = __emit

const exporting = ref(false)
const error = ref('')

// État de sélection (id -> bool)
const selected = reactive<Record<string, boolean>>({})
function applyDefaults() {
  for (const item of EXPORT_CATALOG) selected[item.id] = item.default
}
applyDefaults()

const groups = computed(() => {
  const map = new Map<string, typeof EXPORT_CATALOG>()
  for (const item of EXPORT_CATALOG) {
    if (!map.has(item.group)) map.set(item.group, [])
    map.get(item.group)!.push(item)
  }
  return Array.from(map.entries()).map(([name, items]) => ({ name, items }))
})

const selectedCount = computed(() => Object.values(selected).filter(Boolean).length)

function selectAll(v: boolean) {
  for (const item of EXPORT_CATALOG) selected[item.id] = v
}
function resetDefaults() { applyDefaults() }
function groupAllChecked(group: { items: typeof EXPORT_CATALOG }) {
  return group.items.every((i) => selected[i.id])
}
function toggleGroup(group: { items: typeof EXPORT_CATALOG }, v: boolean) {
  for (const i of group.items) selected[i.id] = v
}
function onGroupToggle(group: { items: typeof EXPORT_CATALOG }, e: Event) {
  toggleGroup(group, (e.target as HTMLInputElement).checked)
}

// ── Fenêtre temporelle + filtres depuis les params du dashboard ──
function resolveFilters(): { filters: ExportFilters; periodLabel: string; filtersLabel: string } {
  const p = props.params || {}
  let fromISO: string, toISO: string, periodLabel: string
  if (p.date) {
    fromISO = `${p.date}T00:00:00`; toISO = `${p.date}T23:59:59`; periodLabel = p.date
  } else if (p.from || p.to) {
    const from = p.from || new Date(Date.now() - 30 * 864e5).toISOString().slice(0, 10)
    const to = p.to || new Date().toISOString().slice(0, 10)
    fromISO = `${from}T00:00:00`; toISO = `${to}T23:59:59`; periodLabel = `${from} → ${to}`
  } else {
    const to = new Date(); const from = new Date(Date.now() - 30 * 864e5)
    fromISO = from.toISOString(); toISO = to.toISOString(); periodLabel = '30 derniers jours'
  }
  const filters: ExportFilters = {
    fromISO, toISO,
    ambassadeId: p.ambassade ? Number(p.ambassade) : null,
    directionId: p.direction ? Number(p.direction) : null,
    roleCode: p.role || null,
    userId: p.user ? Number(p.user) : null,
  }
  const parts: string[] = []
  if (p.ambassade) parts.push('ambassade')
  if (p.direction) parts.push('direction')
  if (p.role) parts.push(`rôle ${p.role}`)
  if (p.user) parts.push('agent')
  return { filters, periodLabel, filtersLabel: parts.join(', ') }
}

const periodLabel = computed(() => resolveFilters().periodLabel)

const periodLabelRef = ref(periodLabel.value)
watch(() => props.open, (o) => { if (o) periodLabelRef.value = resolveFilters().periodLabel })

async function doExport() {
  if (selectedCount.value === 0) return
  exporting.value = true
  error.value = ''
  try {
    const selectedIds = EXPORT_CATALOG.filter((i) => selected[i.id]).map((i) => i.id)
    const { filters, periodLabel: pl, filtersLabel } = resolveFilters()

    // Charger les agrégats de connexion uniquement si nécessaire
    const needLogin = EXPORT_CATALOG.some((i) => selected[i.id] && i.needsLogin)
    const needActivity = EXPORT_CATALOG.some((i) => selected[i.id] && i.needsActivity)
    const login = needLogin ? await fetchLoginAggregates(filters, needActivity) : null

    const meta: ExportMeta = {
      generatedAt: new Date().toLocaleString('fr-FR'),
      periodLabel: pl,
      filtersLabel,
    }
    const count = await buildAndDownload(selectedIds, {
      overview: props.overview,
      courriers: props.courriers,
      departs: props.departs,
      affectations: props.affectations,
      login,
    }, meta)

    if (count === 0) {
      error.value = 'Aucune donnée à exporter pour la sélection.'
      return
    }
    emit('done', count)
    close()
  } catch (e: any) {
    error.value = e?.message || "Échec de l'export."
  } finally {
    exporting.value = false
  }
}

function close() {
  if (exporting.value) return
  emit('close')
}

return (_ctx: any,_cache: any) => {
  return (_ctx.open)
    ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
        _createElementVNode("div", {
          class: "absolute inset-0 bg-black/40",
          onClick: close
        }),
        _createElementVNode("div", _hoisted_2, [
          _createElementVNode("div", _hoisted_3, [
            _createElementVNode("div", _hoisted_4, [
              _createElementVNode("div", _hoisted_5, [
                _createVNode(_unref(FileSpreadsheet), { class: "w-5 h-5 text-emerald-600" })
              ]),
              _cache[2] || (_cache[2] = _createElementVNode("div", null, [
                _createElementVNode("h2", { class: "text-lg font-bold text-dashboard-text" }, "Exporter en Excel"),
                _createElementVNode("p", { class: "text-xs text-dashboard-muted" }, "Choisissez les données à inclure — une feuille par jeu")
              ], -1))
            ]),
            _createElementVNode("button", {
              onClick: close,
              class: "p-1.5 rounded-lg hover:bg-dashboard-bg text-dashboard-muted"
            }, [
              _createVNode(_unref(X), { class: "w-5 h-5" })
            ])
          ]),
          _createElementVNode("div", _hoisted_6, [
            _createElementVNode("span", _hoisted_7, [
              _createElementVNode("strong", _hoisted_8, _toDisplayString(selectedCount.value), 1),
              _createTextVNode(" jeu(x) sélectionné(s) · période : " + _toDisplayString(periodLabel.value), 1)
            ]),
            _createElementVNode("div", _hoisted_9, [
              _createElementVNode("button", {
                onClick: _cache[0] || (_cache[0] = ($event: any) => (selectAll(true))),
                class: "text-xs px-2.5 py-1 rounded-md border border-dashboard-border hover:bg-dashboard-bg"
              }, "Tout cocher"),
              _createElementVNode("button", {
                onClick: _cache[1] || (_cache[1] = ($event: any) => (selectAll(false))),
                class: "text-xs px-2.5 py-1 rounded-md border border-dashboard-border hover:bg-dashboard-bg"
              }, "Tout décocher"),
              _createElementVNode("button", {
                onClick: resetDefaults,
                class: "text-xs px-2.5 py-1 rounded-md border border-dashboard-border hover:bg-dashboard-bg"
              }, "Défauts")
            ])
          ]),
          _createElementVNode("div", _hoisted_10, [
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(groups.value, (group) => {
              return (_openBlock(), _createElementBlock("div", {
                key: group.name
              }, [
                _createElementVNode("div", _hoisted_11, [
                  _createElementVNode("h3", _hoisted_12, _toDisplayString(group.name), 1),
                  _createElementVNode("label", _hoisted_13, [
                    _createElementVNode("input", {
                      type: "checkbox",
                      checked: groupAllChecked(group),
                      onChange: ($event: any) => (onGroupToggle(group, $event)),
                      class: "accent-[#22282F]"
                    }, null, 40, _hoisted_14),
                    _cache[3] || (_cache[3] = _createTextVNode(" Tout le groupe "))
                  ])
                ]),
                _createElementVNode("div", _hoisted_15, [
                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(group.items, (item) => {
                    return (_openBlock(), _createElementBlock("label", {
                      key: item.id,
                      class: _normalizeClass(['flex items-start gap-2.5 p-2.5 rounded-lg border cursor-pointer transition-colors',
                       selected[item.id] ? 'border-[#22282F] bg-[#22282F]/5' : 'border-dashboard-border hover:bg-dashboard-bg'])
                    }, [
                      _withDirectives(_createElementVNode("input", {
                        type: "checkbox",
                        "onUpdate:modelValue": ($event: any) => ((selected[item.id]) = $event),
                        class: "mt-0.5 accent-[#22282F]"
                      }, null, 8, _hoisted_16), [
                        [_vModelCheckbox, selected[item.id]]
                      ]),
                      _createElementVNode("div", _hoisted_17, [
                        _createElementVNode("span", _hoisted_18, _toDisplayString(item.label), 1),
                        (item.needsLogin)
                          ? (_openBlock(), _createElementBlock("span", _hoisted_19, "connexions"))
                          : _createCommentVNode("", true)
                      ])
                    ], 2))
                  }), 128))
                ])
              ]))
            }), 128))
          ]),
          _createElementVNode("div", _hoisted_20, [
            (error.value)
              ? (_openBlock(), _createElementBlock("p", _hoisted_21, _toDisplayString(error.value), 1))
              : (_openBlock(), _createElementBlock("span", _hoisted_22, "Le fichier .xlsx sera téléchargé directement.")),
            _createElementVNode("div", _hoisted_23, [
              _createElementVNode("button", {
                onClick: close,
                class: "px-4 py-2 text-sm rounded-lg border border-dashboard-border hover:bg-dashboard-bg"
              }, "Annuler"),
              _createElementVNode("button", {
                onClick: doExport,
                disabled: exporting.value || selectedCount.value === 0,
                class: "px-4 py-2 text-sm rounded-lg bg-emerald-600 text-white font-medium hover:bg-emerald-700 disabled:opacity-50 flex items-center gap-2"
              }, [
                (exporting.value)
                  ? (_openBlock(), _createBlock(_unref(Loader2), {
                      key: 0,
                      class: "w-4 h-4 animate-spin"
                    }))
                  : (_openBlock(), _createBlock(_unref(Download), {
                      key: 1,
                      class: "w-4 h-4"
                    })),
                _createTextVNode(" " + _toDisplayString(exporting.value ? 'Génération…' : 'Exporter'), 1)
              ], 8, _hoisted_24)
            ])
          ])
        ])
      ]))
    : _createCommentVNode("", true)
}
}

})